/**
 * Email My Cart — bottom sheet + Klaviyo handoff
 *
 * Creates/updates a Klaviyo profile, sets cart_send property,
 * tracks "Email My Cart" with a Shopify cart permalink for the flow email.
 * Tag `cart-send` should be applied by the Klaviyo flow (Client API cannot set tags).
 */
(function () {
  'use strict';

  // The snippet can be rendered more than once per page (e.g. cart page + cart
  // drawer), which loads this script twice. Bind globals only once; the first
  // run initializes every [data-email-cart-sheet] instance already in the DOM.
  if (window.__emailCartSheetLoaded) return;
  window.__emailCartSheetLoaded = true;

  const TAG = 'cart-send';
  const EVENT_NAME = 'Email My Cart';
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const OPEN_THRESHOLD = 0.28;
  const CLOSE_THRESHOLD_PX = 70;

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function getKlaviyo() {
    return window.klaviyo || window._learnq || null;
  }

  function shopOrigin() {
    if (window.Shopify && window.Shopify.shop) {
      return 'https://' + window.Shopify.shop;
    }
    return window.location.origin;
  }

  function clientY(e) {
    if (typeof e.clientY === 'number') return e.clientY;
    if (e.touches && e.touches[0]) return e.touches[0].clientY;
    if (e.changedTouches && e.changedTouches[0]) return e.changedTouches[0].clientY;
    return 0;
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Could not load cart');
    return res.json();
  }

  function buildCartPermalink(cart) {
    if (!cart || !cart.items || !cart.items.length) return null;
    const parts = cart.items.map(function (item) {
      return item.variant_id + ':' + item.quantity;
    });
    // `storefront=true` sends the shopper to the Online Store cart page
    // instead of straight to checkout, which is the default behaviour for
    // Shopify cart permalinks.
    // https://shopify.dev/docs/apps/build/checkout/create-cart-permalinks
    return shopOrigin() + '/cart/' + parts.join(',') + '?storefront=true';
  }

  function cartLineItems(cart) {
    return (cart.items || []).map(function (item) {
      return {
        product_title: item.product_title,
        variant_title: item.variant_title,
        quantity: item.quantity,
        price: item.final_line_price / 100,
        sku: item.sku,
        variant_id: item.variant_id,
        product_id: item.product_id,
        image: item.image,
        url: shopOrigin() + item.url
      };
    });
  }

  async function sendToKlaviyo(email, cart, publicKey) {
    const permalink = buildCartPermalink(cart);
    const items = cartLineItems(cart);
    const now = new Date().toISOString();

    const profileProps = {
      $email: email,
      email: email,
      cart_send: true,
      cart_send_tag: TAG,
      cart_permalink: permalink,
      cart_send_requested_at: now
    };

    const eventProps = {
      cart_permalink: permalink,
      cart_url: permalink,
      item_count: cart.item_count,
      cart_total: (cart.total_price || 0) / 100,
      currency: cart.currency || 'ZAR',
      items: items,
      tag: TAG
    };

    const kl = getKlaviyo();
    if (kl && typeof kl.push === 'function') {
      kl.push(['identify', profileProps]);
      kl.push(['track', EVENT_NAME, eventProps]);
    } else if (kl && typeof kl.identify === 'function') {
      await kl.identify(profileProps);
      if (typeof kl.track === 'function') {
        await kl.track(EVENT_NAME, eventProps);
      }
    }

    if (publicKey) {
      try {
        await fetch(
          'https://a.klaviyo.com/client/profiles/?company_id=' + encodeURIComponent(publicKey),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              revision: '2024-10-15'
            },
            body: JSON.stringify({
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  properties: {
                    cart_send: true,
                    cart_send_tag: TAG,
                    cart_permalink: permalink,
                    cart_send_requested_at: now
                  }
                }
              }
            })
          }
        );
      } catch (err) {
        console.warn('Klaviyo profile upsert skipped', err);
      }

      try {
        await fetch(
          'https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(publicKey),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              revision: '2024-10-15'
            },
            body: JSON.stringify({
              data: {
                type: 'event',
                attributes: {
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: { email: email }
                    }
                  },
                  metric: {
                    data: {
                      type: 'metric',
                      attributes: { name: EVENT_NAME }
                    }
                  },
                  properties: eventProps,
                  time: now
                }
              }
            })
          }
        );
      } catch (err) {
        console.warn('Klaviyo event upsert skipped', err);
      }
    }

    return { permalink: permalink };
  }

  function initSheet(root) {
    if (!root || root.dataset.emailCartInit === 'true') return;
    root.dataset.emailCartInit = 'true';

    const publicKey = root.dataset.klaviyoPublicKey || '';
    const tab = root.querySelector('[data-email-cart-tab]');
    const panel = root.querySelector('[data-email-cart-panel]');
    const backdrop = root.querySelector('[data-email-cart-backdrop]');
    const closeEls = root.querySelectorAll('[data-email-cart-close]');
    const form = root.querySelector('[data-email-cart-form]');
    const input = root.querySelector('[data-email-cart-email]');
    const status = root.querySelector('[data-email-cart-status]');
    const submit = root.querySelector('[data-email-cart-submit]');
    const panelHandle = root.querySelector('[data-email-cart-panel-handle]');
    const dragZone = root.querySelector('[data-email-cart-drag-zone]');

    if (!tab || !panel || !form || !input) return;

    document.body.classList.add('has-email-cart-sheet');

    var drag = null;
    var suppressClick = false;
    var activePointerId = null;

    function setStatus(message, type) {
      if (!status) return;
      status.textContent = message || '';
      status.classList.remove('is-error', 'is-success');
      if (type) status.classList.add(type);
    }

    function panelHeight() {
      return panel.offsetHeight || Math.min(window.innerHeight * 0.85, 560);
    }

    function setPanelOffset(px) {
      panel.style.transform = 'translate3d(0, ' + px + 'px, 0)';
    }

    function clearPanelInline() {
      panel.style.transform = '';
    }

    function open() {
      // Move to the end of <body> so the sheet stacks above late-injected
      // app widgets (WhatsApp/Zoko) that share a max z-index.
      if (document.body) document.body.appendChild(root);
      root.classList.add('is-open');
      root.classList.remove('is-dragging');
      root.setAttribute('aria-expanded', 'true');
      document.body.classList.add('is-email-cart-open');
      clearPanelInline();
    }

    function close() {
      root.classList.remove('is-open', 'is-dragging');
      root.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('is-email-cart-open');
      clearPanelInline();
      setStatus('', null);
      input.classList.remove('is-invalid');
      if (document.activeElement && panel.contains(document.activeElement)) {
        document.activeElement.blur();
      }
    }

    function isOpen() {
      return root.classList.contains('is-open');
    }

    function beginDrag(mode, e) {
      if (drag) return;
      if (e.cancelable) e.preventDefault();
      activePointerId = e.pointerId != null ? e.pointerId : null;
      drag = {
        mode: mode,
        startY: clientY(e),
        lastY: clientY(e),
        moved: false,
        height: panelHeight()
      };
      root.classList.add('is-dragging');
      if (mode === 'open') {
        setPanelOffset(drag.height);
      } else {
        setPanelOffset(0);
      }
    }

    function moveDrag(e) {
      if (!drag) return;
      if (activePointerId != null && e.pointerId != null && e.pointerId !== activePointerId) return;
      if (e.cancelable) e.preventDefault();

      var y = clientY(e);
      var delta = y - drag.startY;
      drag.lastY = y;

      if (Math.abs(delta) > 6) drag.moved = true;

      var offset;
      if (drag.mode === 'open') {
        offset = Math.min(drag.height, Math.max(0, drag.height + delta));
      } else {
        offset = Math.min(drag.height, Math.max(0, delta));
      }
      setPanelOffset(offset);
    }

    function endDrag(e) {
      if (!drag) return;
      if (e && activePointerId != null && e.pointerId != null && e.pointerId !== activePointerId) return;

      var height = drag.height || panelHeight();
      var delta = drag.lastY - drag.startY;

      if (drag.mode === 'open') {
        var currentOffset = Math.min(height, Math.max(0, height + delta));
        var revealed = 1 - currentOffset / height;
        root.classList.remove('is-dragging');
        if (revealed >= OPEN_THRESHOLD || (drag.moved && revealed > 0.15 && delta < 0)) {
          open();
        } else if (!drag.moved) {
          open();
        } else {
          clearPanelInline();
          close();
        }
      } else {
        root.classList.remove('is-dragging');
        // Close if dragged down far enough or with a quick downward flick
        if (delta >= CLOSE_THRESHOLD_PX || (drag.moved && delta > 40)) {
          close();
        } else {
          open();
        }
      }

      if (drag.moved) {
        suppressClick = true;
        setTimeout(function () {
          suppressClick = false;
        }, 350);
      }

      drag = null;
      activePointerId = null;
    }

    function bindDrag(el, mode) {
      if (!el) return;

      el.addEventListener(
        'pointerdown',
        function (e) {
          if (e.button != null && e.button !== 0) return;
          if (mode === 'close' && !isOpen()) return;
          beginDrag(mode, e);
          if (el.setPointerCapture && e.pointerId != null) {
            try {
              el.setPointerCapture(e.pointerId);
            } catch (err) {}
          }
        },
        { passive: false }
      );

      el.addEventListener(
        'pointermove',
        function (e) {
          moveDrag(e);
        },
        { passive: false }
      );

      el.addEventListener('pointerup', endDrag);
      el.addEventListener('pointercancel', endDrag);

      // iOS Safari fallback when PointerEvent is flaky
      el.addEventListener(
        'touchstart',
        function (e) {
          if (window.PointerEvent) return;
          if (mode === 'close' && !isOpen()) return;
          beginDrag(mode, e);
        },
        { passive: false }
      );
      el.addEventListener(
        'touchmove',
        function (e) {
          if (window.PointerEvent) return;
          moveDrag(e);
        },
        { passive: false }
      );
      el.addEventListener('touchend', function (e) {
        if (window.PointerEvent) return;
        endDrag(e);
      });
      el.addEventListener('touchcancel', function (e) {
        if (window.PointerEvent) return;
        endDrag(e);
      });
    }

    bindDrag(tab, 'open');
    bindDrag(dragZone || panelHandle, 'close');

    // Also allow swipe-down when panel is scrolled to top (outside form fields)
    panel.addEventListener(
      'pointerdown',
      function (e) {
        if (!isOpen() || drag) return;
        if (e.target && e.target.closest('input, button, textarea, a, label, form')) return;
        if (dragZone && dragZone.contains(e.target)) return; // already bound
        if (panel.scrollTop > 0) return;
        beginDrag('close', e);
        if (panel.setPointerCapture && e.pointerId != null) {
          try {
            panel.setPointerCapture(e.pointerId);
          } catch (err) {}
        }
      },
      { passive: false }
    );

    panel.addEventListener(
      'pointermove',
      function (e) {
        moveDrag(e);
      },
      { passive: false }
    );
    panel.addEventListener('pointerup', endDrag);
    panel.addEventListener('pointercancel', endDrag);

    window.addEventListener(
      'pointermove',
      function (e) {
        if (!drag) return;
        moveDrag(e);
      },
      { passive: false }
    );
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);

    tab.addEventListener('click', function (e) {
      if (suppressClick) {
        e.preventDefault();
        return;
      }
      open();
    });

    closeEls.forEach(function (el) {
      el.addEventListener('click', function () {
        if (!suppressClick) close();
      });
    });

    if (backdrop) {
      backdrop.addEventListener('click', function () {
        if (!suppressClick) close();
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) close();
    });

    form.addEventListener('submit', async function (e) {
      e.preventDefault();
      const email = (input.value || '').trim().toLowerCase();

      if (!EMAIL_RE.test(email)) {
        input.classList.add('is-invalid');
        setStatus('Please enter a valid email address.', 'is-error');
        input.focus();
        return;
      }

      input.classList.remove('is-invalid');
      setStatus('', null);
      if (submit) {
        submit.disabled = true;
        submit.dataset.originalText = submit.textContent;
        submit.textContent = 'Sending…';
      }

      try {
        const cart = await fetchCart();
        if (!cart.item_count) {
          setStatus('Your cart is empty.', 'is-error');
          return;
        }

        await sendToKlaviyo(email, cart, publicKey);
        setStatus('Done — check your inbox shortly.', 'is-success');
        form.reset();

        setTimeout(close, 1600);
      } catch (err) {
        console.error(err);
        setStatus('Something went wrong. Please try again.', 'is-error');
      } finally {
        if (submit) {
          submit.disabled = false;
          submit.textContent = submit.dataset.originalText || 'Email my cart';
        }
      }
    });

    root.emailCartSheet = { open: open, close: close };
  }

  // External triggers (e.g. the cart drawer button). An element with
  // [data-email-cart-open="<id_suffix>"] opens the matching sheet instance
  // and closes the cart drawer first so the sheet slides up unobstructed.
  function openSheetFor(suffix) {
    var selector = suffix
      ? '[data-email-cart-sheet][data-email-cart-id="' + suffix + '"]'
      : '[data-email-cart-sheet]';
    var sheet = document.querySelector(selector);
    if (!sheet || !sheet.emailCartSheet) return;
    sheet.emailCartSheet.open();
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('[data-email-cart-open]');
    if (!trigger) return;
    e.preventDefault();
    var suffix = trigger.getAttribute('data-email-cart-open') || '';

    var drawer = document.querySelector('cart-drawer.active');
    if (drawer && typeof drawer.close === 'function') {
      drawer.close();
      // Let the drawer close animation start before the sheet slides up.
      setTimeout(function () {
        openSheetFor(suffix);
      }, 180);
    } else {
      openSheetFor(suffix);
    }
  });

  ready(function () {
    document.querySelectorAll('[data-email-cart-sheet]').forEach(initSheet);
  });
})();
